import React, { useEffect, useState, useRef, useCallback } from "react";
import Layout, { PageHeader } from "../components/Layout";
import api, { formatApiError } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Plus, Trash, PencilSimple, CheckCircle, WarningCircle, ArrowsClockwise,
  Folder, File, Upload, House, TerminalWindow, CaretRight,
  DotsThreeVertical, DownloadSimple, FolderSimplePlus, FilePlus, Archive,
  Scissors, CopySimple, MagnifyingGlass, ArrowUp, FileArchive, Copy,
  Globe, NotePencil, FileText,
} from "@phosphor-icons/react";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function AccStatusBadge({ status }) {
  const map = {
    connected: ["success", "Connected"],
    error: ["danger", "Error"],
    unchecked: ["default", "Not tested"],
  };
  const [v, l] = map[status] || ["default", status];
  return <StatusBadge variant={v}>{l}</StatusBadge>;
}

function DomainTypeBadge({ type }) {
  const map = {
    main_domain: ["info", "Main"],
    addon_domain: ["default", "Addon"],
    subdomain: ["warning", "Sub"],
    parked_domain: ["default", "Parked"],
  };
  const [v, l] = map[type] || ["default", type];
  return <StatusBadge variant={v}>{l}</StatusBadge>;
}

const isArchive = (name) => /\.(zip|tar\.gz|tar\.bz2|tar|gz|bz2)$/i.test(name);

// ─── Account Card ─────────────────────────────────────────────────────────────

function AccountCard({ acc, testing, onTest, onEdit, onDelete, onSetDefault }) {
  return (
    <div
      className={`border rounded-lg p-4 bg-card transition-colors ${acc.is_default ? "border-primary/50 bg-primary/5" : "border-border"}`}
      data-testid={`cp-account-${acc.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm">{acc.name}</span>
            {acc.is_default && <StatusBadge variant="info">Default</StatusBadge>}
            <AccStatusBadge status={acc.status} />
          </div>
          <div className="mt-1.5 space-y-0.5">
            <div className="text-xs text-muted-foreground font-mono">{acc.hostname}:{acc.port}</div>
            <div className="text-xs text-muted-foreground">User: <span className="font-mono text-foreground">{acc.username}</span></div>
            <div className="text-xs text-muted-foreground font-mono">Token: {acc.api_token_masked || "••••••••"}</div>
            {acc.notes && <div className="text-xs text-muted-foreground">{acc.notes}</div>}
          </div>
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => onTest(acc.id)} disabled={testing === acc.id} data-testid={`test-cp-${acc.id}`}>
            <ArrowsClockwise size={12} className={testing === acc.id ? "animate-spin mr-1" : "mr-1"} />Test
          </Button>
          {!acc.is_default && (
            <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => onSetDefault(acc.id)}>Set Default</Button>
          )}
          <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => onEdit(acc)}>
            <PencilSimple size={12} className="mr-1" />Edit
          </Button>
          <Button size="sm" variant="ghost" className="text-xs h-7 text-destructive hover:text-destructive" onClick={() => onDelete(acc.id)}>
            <Trash size={12} className="mr-1" />Delete
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Account Form Modal ───────────────────────────────────────────────────────

function AccountModal({ open, onClose, onSaved, editing }) {
  const [form, setForm] = useState({ name: "", hostname: "", port: 2083, username: "", api_token: "", notes: "", is_default: false });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) {
      if (editing) setForm({ name: editing.name, hostname: editing.hostname, port: editing.port, username: editing.username, api_token: "", notes: editing.notes || "", is_default: editing.is_default });
      else setForm({ name: "", hostname: "", port: 2083, username: "", api_token: "", notes: "", is_default: false });
    }
  }, [open, editing]);

  const save = async () => {
    if (!form.name || !form.hostname || !form.username) return toast.error("Name, hostname and username are required");
    if (!editing && !form.api_token) return toast.error("API Token is required");
    setSaving(true);
    try {
      if (editing) await api.put(`/cpanel/accounts/${editing.id}`, form);
      else await api.post("/cpanel/accounts", form);
      toast.success(editing ? "Account updated" : "Account added");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="cp-account-modal">
        <DialogHeader><DialogTitle>{editing ? "Edit cPanel Account" : "Add cPanel Account"}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          {!editing && (
            <div className="bg-muted/50 border rounded p-3 text-xs text-muted-foreground space-y-0.5">
              <div className="font-semibold text-foreground">Create API Token in cPanel:</div>
              <div>cPanel → Security → Manage API Tokens → Create</div>
            </div>
          )}
          <div><label className="overline block mb-1">Connection Name *</label>
            <Input value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Main Hosting" data-testid="cp-name-input" /></div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2"><label className="overline block mb-1">Hostname *</label>
              <Input value={form.hostname} onChange={e => set("hostname", e.target.value)} placeholder="server.host.com" className="font-mono text-xs" data-testid="cp-hostname-input" /></div>
            <div><label className="overline block mb-1">Port</label>
              <Input type="number" value={form.port} onChange={e => set("port", parseInt(e.target.value) || 2083)} className="font-mono" data-testid="cp-port-input" /></div>
          </div>
          <div><label className="overline block mb-1">Username *</label>
            <Input value={form.username} onChange={e => set("username", e.target.value)} placeholder="cpanel username" className="font-mono text-xs" data-testid="cp-username-input" /></div>
          <div><label className="overline block mb-1">API Token {editing ? "(leave blank to keep)" : "*"}</label>
            <Input type="password" value={form.api_token} onChange={e => set("api_token", e.target.value)} placeholder={editing ? "New token to replace current" : "paste API token"} data-testid="cp-token-input" /></div>
          <div><label className="overline block mb-1">Notes</label>
            <Input value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Optional" /></div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.is_default} onChange={e => set("is_default", e.target.checked)} className="h-4 w-4 rounded" />
            Set as default account
          </label>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-cp-account-btn">{saving ? "Saving…" : editing ? "Update" : "Add Account"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Accounts Tab ─────────────────────────────────────────────────────────────

function AccountsTab({ accounts, loading, onRefresh }) {
  const [testing, setTesting] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const handleTest = async id => {
    setTesting(id);
    try {
      const { data } = await api.post(`/cpanel/accounts/${id}/test`);
      toast.success(`Connected — ${data.addon_count} addon domains`);
      onRefresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); onRefresh(); }
    finally { setTesting(null); }
  };

  const handleDelete = async id => {
    try { await api.delete(`/cpanel/accounts/${id}`); toast.success("Account removed"); onRefresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDeleteId(null); }
  };

  const handleSetDefault = async id => {
    try { await api.put(`/cpanel/accounts/${id}`, { is_default: true }); onRefresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <div className="py-16 text-center text-muted-foreground text-sm">Loading…</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-muted-foreground">{accounts.length} account{accounts.length !== 1 ? "s" : ""} connected</p>
        <Button size="sm" onClick={() => { setEditing(null); setModalOpen(true); }} data-testid="add-cp-account-btn">
          <Plus size={14} className="mr-1.5" />Add Account
        </Button>
      </div>
      {accounts.length === 0 ? (
        <div className="border border-dashed rounded-lg py-16 text-center" data-testid="no-cp-accounts">
          <TerminalWindow size={36} className="mx-auto mb-3 opacity-30" />
          <div className="font-semibold mb-1">No cPanel accounts connected</div>
          <p className="text-sm text-muted-foreground mb-4">Add a cPanel account to start managing domains and files.</p>
          <Button onClick={() => { setEditing(null); setModalOpen(true); }}><Plus size={14} className="mr-1.5" />Add your first account</Button>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2" data-testid="cp-accounts-list">
          {accounts.map(a => (
            <AccountCard key={a.id} acc={a} testing={testing}
              onTest={handleTest} onEdit={e => { setEditing(e); setModalOpen(true); }}
              onDelete={setDeleteId} onSetDefault={handleSetDefault} />
          ))}
        </div>
      )}
      <AccountModal open={modalOpen} onClose={() => { setModalOpen(false); setEditing(null); }} onSaved={onRefresh} editing={editing} />
      <Dialog open={!!deleteId} onOpenChange={o => !o && setDeleteId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Remove account?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground py-2">This removes the connection from NameOps. Your cPanel account is unaffected.</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteId)} data-testid="confirm-delete-cp-btn">Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Domains Tab ──────────────────────────────────────────────────────────────

function DomainsTab({ accountId, onOpenFiles }) {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [addForm, setAddForm] = useState({ domain: "", subdomain: "", document_root: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/cpanel/domains?account_id=${accountId}`);
      setDomains(data.domains || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  const filtered = domains.filter(d => {
    const matchSearch = d.domain.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === "all" || d.type === filter;
    return matchSearch && matchFilter;
  });

  const handleAdd = async () => {
    if (!addForm.domain || !addForm.subdomain) return toast.error("Domain and subdomain prefix are required");
    const docroot = addForm.document_root || `/home/user1/public_html/${addForm.domain}`;
    setSaving(true);
    try {
      await api.post("/cpanel/domains/add", { cpanel_account_id: accountId, ...addForm, document_root: docroot });
      toast.success(`Domain ${addForm.domain} added`);
      setAddOpen(false); setAddForm({ domain: "", subdomain: "", document_root: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const handleRemove = async () => {
    try {
      await api.post("/cpanel/domains/remove", { cpanel_account_id: accountId, domain: removeTarget.domain });
      toast.success(`${removeTarget.domain} removed`);
      setRemoveTarget(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!accountId) return <div className="py-16 text-center text-muted-foreground text-sm">Select an account first</div>;

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <div className="relative flex-1 min-w-48">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search domains…" className="pl-8 h-8 text-sm" data-testid="cp-domain-search" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="main_domain">Main</SelectItem>
            <SelectItem value="addon_domain">Addon</SelectItem>
            <SelectItem value="subdomain">Subdomain</SelectItem>
            <SelectItem value="parked_domain">Parked</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={load}>
          <ArrowsClockwise size={12} className="mr-1" />Refresh
        </Button>
        <Button size="sm" className="h-8 text-xs" onClick={() => setAddOpen(true)} data-testid="add-cp-domain-btn">
          <Plus size={12} className="mr-1" />Add Domain
        </Button>
      </div>

      {loading ? (
        <div className="py-10 text-center text-muted-foreground text-sm">Loading domains…</div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="cp-domains-table">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-4 py-2.5 overline text-left">Domain</th>
                <th className="px-4 py-2.5 overline text-left">Type</th>
                <th className="px-4 py-2.5 overline text-left hidden md:table-cell">Document Root</th>
                <th className="px-4 py-2.5 overline text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-10 text-center text-muted-foreground">No domains found</td></tr>
              ) : filtered.map(d => (
                <tr key={d.domain} className="hover:bg-muted/30 transition-colors" data-testid={`cp-domain-row-${d.domain}`}>
                  <td className="px-4 py-3 font-mono text-xs font-medium">{d.domain}</td>
                  <td className="px-4 py-3"><DomainTypeBadge type={d.type} /></td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground hidden md:table-cell truncate max-w-xs">{d.documentroot || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onOpenFiles(d.documentroot || `/home/user1/public_html`)} data-testid={`open-files-${d.domain}`}>
                        <Folder size={12} className="mr-1" />Files
                      </Button>
                      {d.type === "addon_domain" && (
                        <Button size="sm" variant="ghost" className="h-7 text-xs text-destructive hover:text-destructive" onClick={() => setRemoveTarget(d)} data-testid={`remove-domain-${d.domain}`}>
                          <Trash size={12} className="mr-1" />Remove
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Domain Modal */}
      <Dialog open={addOpen} onOpenChange={o => !o && setAddOpen(false)}>
        <DialogContent className="max-w-md" data-testid="add-domain-modal">
          <DialogHeader><DialogTitle>Add Addon Domain</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <div><label className="overline block mb-1">Domain Name *</label>
              <Input value={addForm.domain} onChange={e => setAddForm(f => ({ ...f, domain: e.target.value }))} placeholder="example.com" className="font-mono text-xs" data-testid="add-domain-input" /></div>
            <div><label className="overline block mb-1">Subdomain Prefix *</label>
              <Input value={addForm.subdomain} onChange={e => setAddForm(f => ({ ...f, subdomain: e.target.value }))} placeholder="example (without .domain)" className="font-mono text-xs" data-testid="add-subdomain-input" />
              <p className="text-xs text-muted-foreground mt-1">cPanel uses this to create an alias subdomain internally</p>
            </div>
            <div><label className="overline block mb-1">Document Root</label>
              <Input value={addForm.document_root} onChange={e => setAddForm(f => ({ ...f, document_root: e.target.value }))} placeholder={`/home/user1/public_html/${addForm.domain || "domain.com"}`} className="font-mono text-xs" />
              <p className="text-xs text-muted-foreground mt-1">Leave blank to auto-fill</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={saving} data-testid="confirm-add-domain-btn">{saving ? "Adding…" : "Add Domain"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Confirm */}
      <Dialog open={!!removeTarget} onOpenChange={o => !o && setRemoveTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="remove-domain-modal">
          <DialogHeader><DialogTitle>Remove domain?</DialogTitle></DialogHeader>
          <div className="py-2 space-y-2">
            <p className="text-sm text-muted-foreground">Remove <span className="font-mono font-medium text-foreground">{removeTarget?.domain}</span> from cPanel?</p>
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-2.5 text-xs text-yellow-700 dark:text-yellow-400">
              This removes the domain configuration from cPanel. Files in the document root are not deleted.
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRemoveTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleRemove} data-testid="confirm-remove-domain-btn">Remove Domain</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── File Manager Tab ─────────────────────────────────────────────────────────

function FilesTab({ accountId, initialPath }) {
  const [path, setPath] = useState("");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);
  const [opLoading, setOpLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Modal states
  const [renameModal, setRenameModal] = useState(null);
  const [newNameVal, setNewNameVal] = useState("");
  const [createFileModal, setCreateFileModal] = useState(false);
  const [newFileName, setNewFileName] = useState("");
  const [newFileContent, setNewFileContent] = useState("");
  const [createFolderModal, setCreateFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [deleteModal, setDeleteModal] = useState([]);
  const [moveModal, setMoveModal] = useState([]);
  const [copyModal, setCopyModal] = useState([]);
  const [destPath, setDestPath] = useState("");
  const [compressModal, setCompressModal] = useState([]);
  const [archiveName, setArchiveName] = useState("archive");
  const [archiveType, setArchiveType] = useState("zip");
  const [extractModal, setExtractModal] = useState(null);
  const [extractDest, setExtractDest] = useState("");

  // ── File Editor ───────────────────────────────────────────────────────────
  const [editorFile, setEditorFile] = useState(null); // {name, type}
  const [editorContent, setEditorContent] = useState("");
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorSaving, setEditorSaving] = useState(false);
  const [editorDirty, setEditorDirty] = useState(false);

  // Sync initial path from parent (when domain opens files)
  useEffect(() => {
    if (initialPath) setPath(initialPath);
  }, [initialPath]);

  const loadFiles = useCallback(async (p) => {
    const target = p !== undefined ? p : path;
    if (!accountId || !target) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/cpanel/files?account_id=${accountId}&path=${encodeURIComponent(target)}`);
      setFiles(data.files || []);
      setSelected(new Set());
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [accountId, path]);

  useEffect(() => { loadFiles(); }, [accountId, path]);

  const navigate = (newPath) => {
    setPath(newPath);
    setSelected(new Set());
  };

  const goUp = () => {
    const parts = path.split("/").filter(Boolean);
    if (parts.length <= 1) return;
    navigate("/" + parts.slice(0, -1).join("/"));
  };

  const breadcrumbs = () => {
    const parts = path.split("/").filter(Boolean);
    return parts.map((p, i) => ({ label: p, path: "/" + parts.slice(0, i + 1).join("/") }));
  };

  // Selection
  const allSelected = files.length > 0 && selected.size === files.length;
  const someSelected = selected.size > 0 && !allSelected;

  const toggleSelect = (name) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelected(allSelected ? new Set() : new Set(files.map(f => f.name)));
  };

  // Absolute path helper
  const abs = (name) => `${path.replace(/\/$/, "")}/${name}`;

  // ─── Upload ───────────────────────────────────────────────────────────────

  const handleUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;
    setUploading(true);
    setUploadProgress(selectedFiles.map(f => ({ name: f.name, status: "uploading" })));
    try {
      const form = new FormData();
      form.append("account_id", accountId);
      form.append("path", path);
      selectedFiles.forEach(f => form.append("files", f));
      await api.post("/cpanel/files/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadProgress(prev => prev.map(p => ({ ...p, status: "done" })));
      toast.success(`${selectedFiles.length} file(s) uploaded`);
      setTimeout(() => { setUploadProgress([]); loadFiles(); }, 1500);
    } catch (e) {
      setUploadProgress(prev => prev.map(p => ({ ...p, status: "error" })));
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // ─── File Operations ──────────────────────────────────────────────────────

  const handleCreateFile = async () => {
    if (!newFileName.trim()) return toast.error("Filename is required");
    setOpLoading(true);
    try {
      await api.post("/cpanel/files/create-file", {
        account_id: accountId, dir: path,
        filename: newFileName.trim(), content: newFileContent,
      });
      toast.success(`${newFileName} created`);
      setCreateFileModal(false); setNewFileName(""); setNewFileContent("");
      loadFiles();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setOpLoading(false); }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return toast.error("Folder name is required");
    setOpLoading(true);
    try {
      await api.post("/cpanel/files/create-folder", {
        account_id: accountId, path: path, name: newFolderName.trim(),
      });
      toast.success(`${newFolderName} created`);
      setCreateFolderModal(false); setNewFolderName("");
      loadFiles();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setOpLoading(false); }
  };

  const handleRename = async () => {
    if (!newNameVal.trim()) return toast.error("New name is required");
    setOpLoading(true);
    try {
      await api.post("/cpanel/files/rename", {
        account_id: accountId, dir: path,
        old_name: renameModal.name, new_name: newNameVal.trim(),
      });
      toast.success("Renamed successfully");
      setRenameModal(null); setNewNameVal("");
      loadFiles();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setOpLoading(false); }
  };

  const handleMove = async () => {
    if (!destPath.trim()) return toast.error("Destination path is required");
    setOpLoading(true);
    try {
      await api.post("/cpanel/files/move", {
        account_id: accountId, workingdir: path,
        names: moveModal, destdir: destPath.trim(),
      });
      toast.success(`${moveModal.length} item(s) moved`);
      setMoveModal([]); setDestPath(""); setSelected(new Set());
      loadFiles();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setOpLoading(false); }
  };

  const handleCopy = async () => {
    if (!destPath.trim()) return toast.error("Destination path is required");
    setOpLoading(true);
    try {
      await api.post("/cpanel/files/copy", {
        account_id: accountId, workingdir: path,
        names: copyModal, destdir: destPath.trim(),
      });
      toast.success(`${copyModal.length} item(s) copied`);
      setCopyModal([]); setDestPath(""); setSelected(new Set());
      loadFiles();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setOpLoading(false); }
  };

  const handleDelete = async () => {
    setOpLoading(true);
    try {
      await api.post("/cpanel/files/delete", {
        account_id: accountId, workingdir: path, names: deleteModal,
      });
      toast.success(`${deleteModal.length} item(s) moved to trash`);
      setDeleteModal([]); setSelected(new Set());
      loadFiles();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setOpLoading(false); }
  };

  const handleDownload = async (filename) => {
    try {
      const resp = await api.get("/cpanel/files/download", {
        params: { account_id: accountId, dir: path, filename },
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const handleCompress = async () => {
    if (!archiveName.trim()) return toast.error("Archive name is required");
    const ext = archiveType === "tar.gz" ? ".tar.gz" : archiveType === "tar.bz2" ? ".tar.bz2" : `.${archiveType}`;
    const finalName = archiveName.endsWith(ext) ? archiveName : archiveName + ext;
    setOpLoading(true);
    try {
      await api.post("/cpanel/files/compress", {
        account_id: accountId, workingdir: path,
        names: compressModal, archive_name: finalName, archive_type: archiveType,
      });
      toast.success(`Archive ${finalName} created`);
      setCompressModal([]); setArchiveName("archive"); setArchiveType("zip"); setSelected(new Set());
      loadFiles();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setOpLoading(false); }
  };

  const handleExtract = async () => {
    const dest = extractDest.trim() || path;
    setOpLoading(true);
    try {
      await api.post("/cpanel/files/extract", {
        account_id: accountId, workingdir: path,
        archive_name: extractModal.name, destdir: dest,
      });
      toast.success("Extracted successfully");
      setExtractModal(null); setExtractDest("");
      loadFiles();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setOpLoading(false); }
  };

  // ── File Editor handlers ───────────────────────────────────────────────

  const openEditor = async (file) => {
    setEditorFile(file);
    setEditorContent("");
    setEditorDirty(false);
    setEditorLoading(true);
    try {
      const { data } = await api.get("/cpanel/files/content", {
        params: { account_id: accountId, dir: path, filename: file.name },
      });
      setEditorContent(data.content || "");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load file");
      setEditorFile(null);
    } finally {
      setEditorLoading(false);
    }
  };

  const saveEditorContent = async () => {
    if (!editorFile) return;
    setEditorSaving(true);
    try {
      await api.post("/cpanel/files/save-content", {
        account_id: accountId, dir: path,
        filename: editorFile.name, content: editorContent,
      });
      toast.success(`${editorFile.name} saved`);
      setEditorDirty(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setEditorSaving(false);
    }
  };

  const closeEditor = () => {
    if (editorDirty && !window.confirm("You have unsaved changes. Discard them?")) return;
    setEditorFile(null);
    setEditorContent("");
    setEditorDirty(false);
  };

  // ─── Actions helpers ──────────────────────────────────────────────────────

  const openRename = (f) => { setRenameModal(f); setNewNameVal(f.name); };
  const openMove = (names) => { setMoveModal(names); setDestPath(path); };
  const openCopy = (names) => { setCopyModal(names); setDestPath(path); };
  const openDelete = (names) => setDeleteModal(names);
  const openCompress = (names) => { setCompressModal(names); setArchiveName("archive"); };
  const selNames = () => Array.from(selected);

  if (!accountId) return (
    <div className="py-16 text-center text-muted-foreground text-sm">Select an account first</div>
  );

  return (
    <div data-testid="cp-file-manager">

      {/* ── Breadcrumb + Toolbar ─────────────────────────────────────────── */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {/* Go up button */}
        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 shrink-0" onClick={goUp} title="Go up" data-testid="go-up-btn">
          <ArrowUp size={14} />
        </Button>
        {/* Breadcrumb */}
        <div className="flex items-center gap-1 flex-1 min-w-0 bg-muted/50 border rounded px-3 py-1.5 font-mono text-xs overflow-x-auto">
          <button onClick={() => navigate("/")} className="hover:text-primary shrink-0" title="Root"><House size={12} /></button>
          {breadcrumbs().map((b) => (
            <span key={b.path} className="flex items-center gap-1">
              <CaretRight size={10} className="text-muted-foreground shrink-0" />
              <button onClick={() => navigate(b.path)} className="hover:text-primary whitespace-nowrap">{b.label}</button>
            </span>
          ))}
        </div>
      </div>

      {/* ── Action Toolbar ───────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setCreateFileModal(true); setNewFileName(""); setNewFileContent(""); }} data-testid="new-file-btn">
          <FilePlus size={12} className="mr-1" />New File
        </Button>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setCreateFolderModal(true); setNewFolderName(""); }} data-testid="new-folder-btn">
          <FolderSimplePlus size={12} className="mr-1" />New Folder
        </Button>
        <div>
          <input type="file" ref={fileInputRef} multiple className="hidden" onChange={handleUpload} data-testid="cp-file-input" />
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={uploading} onClick={() => fileInputRef.current?.click()} data-testid="cp-upload-btn">
            <Upload size={12} className="mr-1" />{uploading ? "Uploading…" : "Upload"}
          </Button>
        </div>
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => loadFiles()} disabled={loading}>
          <ArrowsClockwise size={12} className={loading ? "animate-spin mr-1" : "mr-1"} />Refresh
        </Button>

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div className="flex items-center gap-1.5 ml-auto bg-primary/5 border border-primary/20 rounded px-2.5 py-1" data-testid="bulk-action-bar">
            <span className="text-xs text-primary font-medium">{selected.size} selected</span>
            <div className="w-px h-4 bg-border mx-1" />
            <Button size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={() => openDelete(selNames())} data-testid="bulk-delete-btn">
              <Trash size={11} className="mr-1 text-destructive" />Delete
            </Button>
            <Button size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={() => openMove(selNames())} data-testid="bulk-move-btn">
              <Scissors size={11} className="mr-1" />Move
            </Button>
            <Button size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={() => openCopy(selNames())} data-testid="bulk-copy-btn">
              <CopySimple size={11} className="mr-1" />Copy
            </Button>
            <Button size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={() => openCompress(selNames())} data-testid="bulk-compress-btn">
              <Archive size={11} className="mr-1" />Compress
            </Button>
            <Button size="sm" variant="ghost" className="h-6 text-xs px-2 text-muted-foreground" onClick={() => setSelected(new Set())}>
              Clear
            </Button>
          </div>
        )}
      </div>

      {/* ── Upload progress ──────────────────────────────────────────────── */}
      {uploadProgress.length > 0 && (
        <div className="mb-3 border rounded p-2 space-y-1 bg-muted/30">
          {uploadProgress.map((p, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              {p.status === "uploading" && <ArrowsClockwise size={12} className="animate-spin text-primary" />}
              {p.status === "done" && <CheckCircle size={12} className="text-green-500" weight="fill" />}
              {p.status === "error" && <WarningCircle size={12} className="text-red-500" weight="fill" />}
              <span className="font-mono">{p.name}</span>
              <span className="text-muted-foreground">{p.status}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── File Table ───────────────────────────────────────────────────── */}
      {loading ? (
        <div className="py-10 text-center text-muted-foreground text-sm">Loading files…</div>
      ) : files.length === 0 ? (
        <div className="border border-dashed rounded py-12 text-center text-muted-foreground text-sm">
          Empty directory — use the toolbar above to create files or upload.
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="cp-files-table">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-3 py-2.5 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={el => el && (el.indeterminate = someSelected)}
                    onChange={toggleSelectAll}
                    className="h-3.5 w-3.5 rounded"
                    data-testid="select-all-checkbox"
                  />
                </th>
                <th className="px-3 py-2.5 overline text-left">Name</th>
                <th className="px-3 py-2.5 overline text-left hidden md:table-cell w-24">Size</th>
                <th className="px-3 py-2.5 overline text-left hidden lg:table-cell w-32">Modified</th>
                <th className="px-3 py-2.5 overline text-left hidden md:table-cell w-24">Permissions</th>
                <th className="px-3 py-2.5 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {files.map((f, i) => {
                const isDir = f.type === "dir";
                const archive = isArchive(f.name);
                return (
                  <tr
                    key={i}
                    className={`hover:bg-muted/30 transition-colors ${selected.has(f.name) ? "bg-primary/5" : ""}`}
                    data-testid={`cp-file-${f.name}`}
                  >
                    <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.has(f.name)}
                        onChange={() => toggleSelect(f.name)}
                        className="h-3.5 w-3.5 rounded"
                        data-testid={`select-${f.name}`}
                      />
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        {isDir
                          ? <Folder size={14} className="text-yellow-500 shrink-0" weight="fill" />
                          : archive
                            ? <FileArchive size={14} className="text-blue-500 shrink-0" />
                            : <File size={14} className="text-muted-foreground shrink-0" />}
                        {isDir ? (
                          <button
                            className="font-mono text-xs text-primary font-medium hover:underline"
                            onClick={() => navigate(`${path.replace(/\/$/, "")}/${f.name}`)}
                            data-testid={`enter-folder-${f.name}`}
                          >
                            {f.name}
                          </button>
                        ) : (
                          <button
                            className="font-mono text-xs hover:text-primary hover:underline text-left"
                            onClick={() => openEditor(f)}
                            title="Click to edit"
                            data-testid={`open-file-${f.name}`}
                          >
                            {f.name}
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground hidden md:table-cell">
                      {isDir ? "—" : f.humansize || "—"}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground hidden lg:table-cell">
                      {f.mtime ? new Date(f.mtime * 1000).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs text-muted-foreground hidden md:table-cell">
                      {f.nicemode || "—"}
                    </td>
                    <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" data-testid={`action-menu-${f.name}`}>
                            <DotsThreeVertical size={14} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40">
                          {!isDir && (
                            <DropdownMenuItem onClick={() => openEditor(f)} data-testid={`edit-${f.name}`}>
                              <NotePencil size={13} className="mr-2" />Edit
                            </DropdownMenuItem>
                          )}
                          {!isDir && (
                            <DropdownMenuItem onClick={() => handleDownload(f.name)} data-testid={`download-${f.name}`}>
                              <DownloadSimple size={13} className="mr-2" />Download
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => openRename(f)} data-testid={`rename-${f.name}`}>
                            <PencilSimple size={13} className="mr-2" />Rename
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openMove([f.name])} data-testid={`move-${f.name}`}>
                            <Scissors size={13} className="mr-2" />Move to…
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openCopy([f.name])} data-testid={`copy-${f.name}`}>
                            <CopySimple size={13} className="mr-2" />Copy to…
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openCompress([f.name])} data-testid={`compress-${f.name}`}>
                            <Archive size={13} className="mr-2" />Compress
                          </DropdownMenuItem>
                          {archive && (
                            <DropdownMenuItem onClick={() => { setExtractModal(f); setExtractDest(path); }} data-testid={`extract-${f.name}`}>
                              <FileArchive size={13} className="mr-2" />Extract
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => openDelete([f.name])}
                            data-testid={`delete-${f.name}`}
                          >
                            <Trash size={13} className="mr-2" />Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Rename Modal ─────────────────────────────────────────────────── */}
      <Dialog open={!!renameModal} onOpenChange={o => !o && setRenameModal(null)}>
        <DialogContent className="max-w-sm" data-testid="rename-modal">
          <DialogHeader><DialogTitle>Rename</DialogTitle></DialogHeader>
          <div className="py-2">
            <label className="overline block mb-1">New name</label>
            <Input
              value={newNameVal}
              onChange={e => setNewNameVal(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleRename()}
              placeholder="new-name.ext"
              className="font-mono text-xs"
              data-testid="rename-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRenameModal(null)}>Cancel</Button>
            <Button onClick={handleRename} disabled={opLoading} data-testid="confirm-rename-btn">Rename</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Create File Modal ────────────────────────────────────────────── */}
      <Dialog open={createFileModal} onOpenChange={o => !o && setCreateFileModal(false)}>
        <DialogContent className="max-w-md" data-testid="create-file-modal">
          <DialogHeader><DialogTitle>Create New File</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="overline block mb-1">Filename *</label>
              <Input
                value={newFileName}
                onChange={e => setNewFileName(e.target.value)}
                placeholder="index.html"
                className="font-mono text-xs"
                data-testid="new-filename-input"
              />
            </div>
            <div>
              <label className="overline block mb-1">Initial Content (optional)</label>
              <Textarea
                value={newFileContent}
                onChange={e => setNewFileContent(e.target.value)}
                placeholder="Leave blank to create an empty file"
                className="font-mono text-xs h-28 resize-none"
                data-testid="new-file-content"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCreateFileModal(false)}>Cancel</Button>
            <Button onClick={handleCreateFile} disabled={opLoading} data-testid="confirm-create-file-btn">Create File</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Create Folder Modal ──────────────────────────────────────────── */}
      <Dialog open={createFolderModal} onOpenChange={o => !o && setCreateFolderModal(false)}>
        <DialogContent className="max-w-sm" data-testid="create-folder-modal">
          <DialogHeader><DialogTitle>Create New Folder</DialogTitle></DialogHeader>
          <div className="py-2">
            <label className="overline block mb-1">Folder name *</label>
            <Input
              value={newFolderName}
              onChange={e => setNewFolderName(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleCreateFolder()}
              placeholder="new-folder"
              className="font-mono text-xs"
              data-testid="new-folder-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCreateFolderModal(false)}>Cancel</Button>
            <Button onClick={handleCreateFolder} disabled={opLoading} data-testid="confirm-create-folder-btn">Create Folder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirmation ──────────────────────────────────────────── */}
      <Dialog open={deleteModal.length > 0} onOpenChange={o => !o && setDeleteModal([])}>
        <DialogContent className="max-w-sm" data-testid="delete-confirm-modal">
          <DialogHeader><DialogTitle>Move to trash?</DialogTitle></DialogHeader>
          <div className="py-2 space-y-2">
            <p className="text-sm text-muted-foreground">
              {deleteModal.length === 1
                ? <><span className="font-mono font-medium text-foreground">{deleteModal[0]}</span> will be moved to .trash</>
                : <><span className="font-medium text-foreground">{deleteModal.length} items</span> will be moved to .trash</>
              }
            </p>
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-2 text-xs text-yellow-700 dark:text-yellow-400">
              Files are moved to the .trash folder in your cPanel account.
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteModal([])}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={opLoading} data-testid="confirm-delete-btn">
              Move to Trash
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Move Modal ───────────────────────────────────────────────────── */}
      <Dialog open={moveModal.length > 0} onOpenChange={o => !o && setMoveModal([])}>
        <DialogContent className="max-w-md" data-testid="move-modal">
          <DialogHeader><DialogTitle>Move {moveModal.length} item(s)</DialogTitle></DialogHeader>
          <div className="py-2">
            <label className="overline block mb-1">Destination path *</label>
            <Input
              value={destPath}
              onChange={e => setDestPath(e.target.value)}
              placeholder="/home/user1/public_html/subfolder"
              className="font-mono text-xs"
              data-testid="move-dest-input"
            />
            <p className="text-xs text-muted-foreground mt-1">Enter the full absolute destination path.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setMoveModal([])}>Cancel</Button>
            <Button onClick={handleMove} disabled={opLoading} data-testid="confirm-move-btn">Move</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Copy Modal ───────────────────────────────────────────────────── */}
      <Dialog open={copyModal.length > 0} onOpenChange={o => !o && setCopyModal([])}>
        <DialogContent className="max-w-md" data-testid="copy-modal">
          <DialogHeader><DialogTitle>Copy {copyModal.length} item(s)</DialogTitle></DialogHeader>
          <div className="py-2">
            <label className="overline block mb-1">Destination path *</label>
            <Input
              value={destPath}
              onChange={e => setDestPath(e.target.value)}
              placeholder="/home/user1/public_html/subfolder"
              className="font-mono text-xs"
              data-testid="copy-dest-input"
            />
            <p className="text-xs text-muted-foreground mt-1">Enter the full absolute destination path.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCopyModal([])}>Cancel</Button>
            <Button onClick={handleCopy} disabled={opLoading} data-testid="confirm-copy-btn">Copy</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Compress Modal ───────────────────────────────────────────────── */}
      <Dialog open={compressModal.length > 0} onOpenChange={o => !o && setCompressModal([])}>
        <DialogContent className="max-w-sm" data-testid="compress-modal">
          <DialogHeader><DialogTitle>Compress {compressModal.length} item(s)</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="overline block mb-1">Archive name *</label>
              <Input
                value={archiveName}
                onChange={e => setArchiveName(e.target.value)}
                placeholder="archive"
                className="font-mono text-xs"
                data-testid="archive-name-input"
              />
            </div>
            <div>
              <label className="overline block mb-1">Archive type</label>
              <Select value={archiveType} onValueChange={setArchiveType}>
                <SelectTrigger className="h-8 text-xs" data-testid="archive-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="zip">ZIP (.zip)</SelectItem>
                  <SelectItem value="tar.gz">TAR.GZ (.tar.gz)</SelectItem>
                  <SelectItem value="tar.bz2">TAR.BZ2 (.tar.bz2)</SelectItem>
                  <SelectItem value="tar">TAR (.tar)</SelectItem>
                  <SelectItem value="gz">GZ (.gz)</SelectItem>
                  <SelectItem value="bz2">BZ2 (.bz2)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCompressModal([])}>Cancel</Button>
            <Button onClick={handleCompress} disabled={opLoading} data-testid="confirm-compress-btn">Compress</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Extract Modal ────────────────────────────────────────────────── */}
      <Dialog open={!!extractModal} onOpenChange={o => !o && setExtractModal(null)}>
        <DialogContent className="max-w-md" data-testid="extract-modal">
          <DialogHeader><DialogTitle>Extract archive</DialogTitle></DialogHeader>
          <div className="py-2">
            <p className="text-sm text-muted-foreground mb-3">
              Extract <span className="font-mono font-medium text-foreground">{extractModal?.name}</span>
            </p>
            <label className="overline block mb-1">Destination path</label>
            <Input
              value={extractDest}
              onChange={e => setExtractDest(e.target.value)}
              placeholder={path}
              className="font-mono text-xs"
              data-testid="extract-dest-input"
            />
            <p className="text-xs text-muted-foreground mt-1">Leave blank to extract to the current directory.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setExtractModal(null)}>Cancel</Button>
            <Button onClick={handleExtract} disabled={opLoading} data-testid="confirm-extract-btn">Extract</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── File Editor ──────────────────────────────────────────────────── */}
      <Dialog open={!!editorFile} onOpenChange={o => !o && closeEditor()}>
        <DialogContent
          className="max-w-5xl w-[90vw] flex flex-col gap-0 p-0 overflow-hidden"
          style={{ height: "88vh", maxHeight: "88vh" }}
          data-testid="file-editor-modal"
        >
          {/* Editor header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30 shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <FileText size={15} className="text-primary shrink-0" />
              <span className="font-mono text-sm font-medium truncate">
                {path.replace(/^.*\/([^/]+)$/, "$1")}/{editorFile?.name}
              </span>
              {editorDirty && (
                <span className="text-xs text-yellow-500 shrink-0">● Unsaved</span>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs text-muted-foreground hidden sm:block">
                {editorContent.split("\n").length} lines · {editorContent.length} chars
              </span>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={closeEditor}
                data-testid="editor-close-btn"
              >
                Close
              </Button>
              <Button
                size="sm"
                className="h-7 text-xs"
                onClick={saveEditorContent}
                disabled={editorSaving || !editorDirty}
                data-testid="editor-save-btn"
              >
                {editorSaving ? (
                  <><ArrowsClockwise size={11} className="animate-spin mr-1" />Saving…</>
                ) : (
                  "Save"
                )}
              </Button>
            </div>
          </div>

          {/* Editor body */}
          <div className="flex-1 overflow-hidden relative">
            {editorLoading ? (
              <div className="flex items-center justify-center h-full gap-2 text-muted-foreground">
                <ArrowsClockwise size={18} className="animate-spin" />
                <span className="text-sm">Loading file…</span>
              </div>
            ) : (
              <textarea
                value={editorContent}
                onChange={e => { setEditorContent(e.target.value); setEditorDirty(true); }}
                className="w-full h-full font-mono text-sm resize-none focus:outline-none p-4 leading-6"
                style={{
                  background: "hsl(220 13% 10%)",
                  color: "hsl(210 40% 90%)",
                  caretColor: "hsl(210 80% 70%)",
                  tabSize: 2,
                }}
                spellCheck={false}
                autoCapitalize="off"
                autoCorrect="off"
                autoComplete="off"
                data-testid="file-editor-textarea"
              />
            )}
          </div>

          {/* Editor status bar */}
          <div className="flex items-center gap-4 px-4 py-1.5 border-t border-border bg-muted/30 text-xs text-muted-foreground shrink-0">
            <span>UTF-8</span>
            <span className="sm:hidden">{editorContent.split("\n").length} lines</span>
            <span className="ml-auto">{editorFile?.name}</span>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function CpanelPage() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [activeTab, setActiveTab] = useState("accounts");
  const [fileManagerPath, setFileManagerPath] = useState("");

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cpanel/accounts");
      const accs = data.accounts || [];
      setAccounts(accs);
      if (!selectedAccount) {
        const def = accs.find(a => a.is_default) || accs[0];
        if (def) setSelectedAccount(def.id);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadAccounts(); }, []);

  const handleOpenFiles = (path) => {
    setFileManagerPath(path);
    setActiveTab("files");
  };

  const selectedAcc = accounts.find(a => a.id === selectedAccount);
  const defaultFilePath = selectedAcc ? `/home/${selectedAcc.username}/public_html` : "";

  return (
    <Layout>
      <PageHeader
        eyebrow="Hosting"
        title="cPanel"
        description="Manage cPanel accounts, domains, and files directly from NameOps."
        actions={
          accounts.length > 1 ? (
            <Select value={selectedAccount} onValueChange={setSelectedAccount}>
              <SelectTrigger className="h-8 w-56 text-xs" data-testid="cp-account-selector">
                <SelectValue placeholder="Select account" />
              </SelectTrigger>
              <SelectContent>
                {accounts.map(a => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name} {a.is_default ? "(default)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null
        }
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-3 w-full max-w-sm">
          <TabsTrigger value="accounts" data-testid="tab-cp-accounts">Accounts</TabsTrigger>
          <TabsTrigger value="domains" data-testid="tab-cp-domains">Domains</TabsTrigger>
          <TabsTrigger value="files" data-testid="tab-cp-files">Files</TabsTrigger>
        </TabsList>

        <TabsContent value="accounts">
          <AccountsTab accounts={accounts} loading={loading} onRefresh={loadAccounts} />
        </TabsContent>

        <TabsContent value="domains">
          <DomainsTab accountId={selectedAccount} onOpenFiles={handleOpenFiles} />
        </TabsContent>

        <TabsContent value="files">
          <FilesTab
            accountId={selectedAccount}
            initialPath={fileManagerPath || defaultFilePath}
          />
        </TabsContent>
      </Tabs>
    </Layout>
  );
}
